import {
  ChartTooltipContent,
  cn,
  GappedChart,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TabsFluid,
} from "web-design-system";
import { AllocationBarRow } from "web-core";
import type {
  AllocationBreakdownBucket,
  FundBasketAnalytics,
} from "creso-core/types";
import {
  ALLOCATION_PCT_THRESHOLD,
  formatAsPercentage,
  withScaledBarPct,
} from "creso-core/utils";
import { useState } from "react";

const RETURN_PERIODS = ["1M", "3M", "1Y", "3Y", "5Y", "10Y"] as const;

const BUCKET_COLORS: Record<string, string> = {
  indian_equity: "var(--color-chart-1)",
  debt: "var(--color-chart-2)",
  commodity: "var(--color-chart-3)",
  global: "var(--color-chart-4)",
};

function formatSignedOneDecimal(value: number | null) {
  if (value === null || value === undefined) return "-";
  const n = Number(value);
  const s = n.toFixed(1);
  if (n > 0) return `+${s}`;
  if (n < 0) return s;
  return s;
}

function returnValueClass(value: number | null) {
  if (value === null) return "text-muted-foreground";
  if (value > 0) return "text-constructive";
  if (value < 0) return "text-destructive";
  return "text-muted-foreground";
}

export function WeightedReturnsTable({
  weighted_returns,
  benchmarkLabel,
}: {
  weighted_returns: FundBasketAnalytics["weighted_returns"];
  benchmarkLabel: string;
}) {
  return (
    <div className="flex flex-col gap-4">
      {/* <span className="text-base font-semibold text-foreground">
        Weighted returns
      </span> */}
      <div className="overflow-hidden">
        <Table className="w-full text-sm">
          <TableHeader>
            <TableRow className="border-b border-border">
              <TableHead className="w-1/3" />
              <TableHead className="text-left">
                <div className="flex items-center gap-2">
                  <span className="inline-block size-3 rounded-sm bg-chart-3" />
                  <span className="text-sm font-medium">Basket portfolio</span>
                </div>
              </TableHead>
              <TableHead className="text-left">
                <div className="flex items-center gap-2">
                  <span className="inline-block w-4 border-t-2 border-dashed border-muted-foreground" />
                  <span className="text-sm font-medium">{benchmarkLabel}</span>
                </div>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody className="divide-y divide-border">
            {RETURN_PERIODS.map((p) => (
              <TableRow key={p}>
                <TableCell className="font-medium">{p}</TableCell>
                <TableCell
                  className={cn(
                    "text-left tabular-nums",
                    returnValueClass(weighted_returns.basket[p])
                  )}
                >
                  {formatSignedOneDecimal(weighted_returns.basket[p])}
                </TableCell>
                <TableCell
                  className={cn(
                    "text-left tabular-nums",
                    returnValueClass(weighted_returns.benchmark[p])
                  )}
                >
                  {formatSignedOneDecimal(weighted_returns.benchmark[p])}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export function BasketAnalyticsSection({
  buckets: allBuckets,
}: {
  buckets: AllocationBreakdownBucket[];
}) {
  const buckets = allBuckets.filter(
    (bucket) => bucket.pct > ALLOCATION_PCT_THRESHOLD
  );

  const [activeBucketKey, setActiveBucketKey] = useState(() => buckets[0]?.key);
  const activeBucket =
    buckets.find((bucket) => bucket.key === activeBucketKey) ?? buckets[0];

  if (buckets.length === 0) return null;

  const allocationTabs = buckets.map((bucket) => ({
    value: bucket.key,
    title: (
      <div className="flex items-center gap-2">
        <span
          className="w-3 h-3 rounded shrink-0"
          style={{ backgroundColor: BUCKET_COLORS[bucket.key] }}
        />
        <span className="font-semibold">{bucket.label}</span>
        <span className="text-muted-foreground">
          {formatAsPercentage(bucket.pct, "-", 1)}
        </span>
      </div>
    ),
    // Rendered outside the tab content below, so rows persist across tab
    // switches and recharts animates between values instead of remounting.
    body: null,
  }));

  const activeSubBuckets = withScaledBarPct(
    (activeBucket?.sub_buckets ?? []).filter(
      (s) => s.pct > ALLOCATION_PCT_THRESHOLD
    )
  );

  // Rank each row by pct (0 = highest) without sorting the rendered array,
  // so opacity steps stay evenly spaced even when pct values are close.
  const activeSubBucketRanks = activeSubBuckets.map(
    (subBucket) =>
      activeSubBuckets.filter((other) => other.pct > subBucket.pct).length
  );

  return (
    <div className="relative flex flex-col gap-4 z-2">
      <span className="text-base font-semibold text-foreground">
        Asset Allocation
      </span>
      <GappedChart
        segments={buckets.map((bucket) => ({
          key: bucket.key,
          value: bucket.pct,
          color: BUCKET_COLORS[bucket.key],
          label: bucket.label ?? bucket.key,
        }))}
        tooltipContent={
          <ChartTooltipContent
            indicator="dot"
            labelFormatter={() => "Asset Allocation"}
            valueFormatter={(value) => `${value}%`}
          />
        }
      />
      <div className="flex flex-col gap-px">
        <TabsFluid
          tabs={allocationTabs}
          value={activeBucketKey}
          onValueChange={setActiveBucketKey}
          contentClassName="hidden"
        />
        {activeBucket && (
          <div
            className={cn(
              "-mt-px flex flex-col gap-4 rounded-lg border border-border p-4",
              activeBucket.key === buckets[0]?.key && "rounded-tl-none"
            )}
          >
            <span className="text-sm text-muted-foreground">
              Breakdown by{" "}
              <span className="text-foreground font-medium">
                {activeBucket.breakdown_by}
              </span>
            </span>
            {activeSubBuckets.map((subBucket, index) => (
              <AllocationBarRow
                key={index}
                label={subBucket.label}
                pct={subBucket.pct}
                barPct={subBucket.barPct}
                color={BUCKET_COLORS[activeBucket.key]}
                opacity={
                  activeSubBuckets.length > 1
                    ? 1 -
                      (activeSubBucketRanks[index] /
                        (activeSubBuckets.length - 1)) *
                        0.5
                    : 1
                }
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
